import _taggedTemplateLiteral from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";
import _objectWithoutProperties from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

var _excluded = ["actionButtons", "children", "className", "fullScreenMobile", "hasCloseButton", "isOpen", "maxWidth", "onClose", "shouldFocusAfterRender", "shouldCloseOnEsc", "testId", "title", "aria", "style", "overlayRef", "bgColor", "wrapContent"];
import { transparentize } from 'polished';
import { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { cloneDeep } from 'lodash-es';
import { useTranslation } from '@yoweb/next-i18next';
import { theme } from '@yoweb/ui/styles';
import { breakpoints, media } from '@yoweb/ui/styles/utils';
import { getRadii, getSpace, getZIndex } from '@yoweb/ui/styles/utils/theme';
import { isServerRendered } from '@yoweb/utils/isServerRendered';
import { ButtonIcon } from '@yoweb/ui/components/buttons/ButtonIcon';
import { CloseIcon } from '@yoweb/ui/components/Icon/CloseIcon';
import { Box } from '@yoweb/ui/components/Box';
import { maxWidthMixin } from '@yoweb/ui/styles/utils/mixins.utils';
import { Grid } from '@yoweb/ui/components/Grid';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

if (isServerRendered === false && true && document.getElementById('__next')) {
  ReactModal.setAppElement('#__next');
}

var MODAL_MAX_WIDTH = 600;

var getHeight = function getHeight() {
  if (!(true && typeof document !== 'undefined')) return null;
  return window.innerHeight;
};

export var createStyle = function createStyle() {
  return {
    /* stylelint-disable-next-line selector-type-no-unknown */
    overlay: {
      background: transparentize(0.4, theme.colors.base400),
      display: 'flex',
      justifyContent: 'center',
      overflowY: 'auto',
      backdropFilter: 'blur(4px)',
      zIndex: theme.zIndices.modal
    },
    content: {
      background: 'none',
      border: 'none',
      borderRadius: 0,
      display: 'flex',
      minHeight: '100vh',
      justifyContent: 'center',
      margin: 'auto',
      padding: 0,
      position: 'static',
      width: '100%'
    }
  };
};
var transitionEnterTime = 400;
var transitionExitTime = 200;
export var modalAnimationVariants = {
  initial: {
    opacity: 0,
    scale: 0.9
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: transitionEnterTime / 1000,
      type: 'spring'
    }
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: transitionExitTime / 1000
    }
  }
};
/**
 * A general modal component.
 */

export var Modal = function Modal(_ref) {
  var actionButtons = _ref.actionButtons,
      children = _ref.children,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$fullScreenMobile = _ref.fullScreenMobile,
      fullScreenMobile = _ref$fullScreenMobile === void 0 ? true : _ref$fullScreenMobile,
      hasCloseButton = _ref.hasCloseButton,
      isOpen = _ref.isOpen,
      _ref$maxWidth = _ref.maxWidth,
      maxWidth = _ref$maxWidth === void 0 ? MODAL_MAX_WIDTH : _ref$maxWidth,
      onClose = _ref.onClose,
      _ref$shouldFocusAfter = _ref.shouldFocusAfterRender,
      shouldFocusAfterRender = _ref$shouldFocusAfter === void 0 ? undefined : _ref$shouldFocusAfter,
      _ref$shouldCloseOnEsc = _ref.shouldCloseOnEsc,
      shouldCloseOnEsc = _ref$shouldCloseOnEsc === void 0 ? true : _ref$shouldCloseOnEsc,
      testId = _ref.testId,
      title = _ref.title,
      aria = _ref.aria,
      style = _ref.style,
      _overlayRef = _ref.overlayRef,
      bgColor = _ref.bgColor,
      _ref$wrapContent = _ref.wrapContent,
      wrapContent = _ref$wrapContent === void 0 ? true : _ref$wrapContent,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(Boolean(isOpen)),
      isModalOpen = _useState[0],
      setIsModalOpen = _useState[1];

  var _useState2 = useState(getHeight),
      height = _useState2[0],
      setHeight = _useState2[1];

  var _useState3 = useState(true),
      isFirstRender = _useState3[0],
      setsIsFirstRender = _useState3[1];

  useEffect(function () {
    if (isFirstRender) {
      setsIsFirstRender(false);
      return;
    }

    var setNextHeight = function setNextHeight() {
      var nextHeight = getHeight();
      setHeight(nextHeight);
    };

    setNextHeight();
    window.addEventListener('resize', setNextHeight, {
      passive: true
    });
    return function () {
      return window.removeEventListener('resize', setNextHeight);
    };
  }, [isFirstRender]);

  var onModalClose = function onModalClose() {
    return onClose ? onClose() : setIsModalOpen(false);
  };

  useEffect(function () {
    // It seems incorrect that we're removing the ReactModal
    // just for the sake of the AnimatePresence animation.
    // This modal expects to be shown/hidden based on its isOpen prop.
    // For some reason on React v18, it stopped cleaning up its bodyOpenClassName
    // when removed from the DOM.
    if (!isOpen) {
      setTimeout(function () {
        return document.body.classList.remove('no-scroll');
      }, transitionExitTime);
    } else {
      // was considering using transitionEnterTime here with a setTimeout,
      // but it looks better without it
      document.body.classList.add('no-scroll');
    }

    if (isModalOpen !== undefined) {
      setIsModalOpen(Boolean(isOpen));
    } // in case this component gets removed from the DOM instead of using the isOpen prop


    return function () {
      return document.body.classList.remove('no-scroll');
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  var mergedStyle = style ? cloneDeep(style) : createStyle();

  if (mergedStyle !== null && mergedStyle !== void 0 && mergedStyle.content) {
    mergedStyle.content.minHeight = height ? "".concat(height, "px") : '100vh';
  }

  var modalBody = /*#__PURE__*/_jsxs(Aside, {
    animate: "animate",
    exit: "exit",
    initial: "initial",
    variants: modalAnimationVariants,
    "data-testid": "modal-animation-container",
    children: [hasCloseButton && /*#__PURE__*/_jsx(Box, {
      position: "relative",
      height: "large1",
      children: /*#__PURE__*/_jsx(CloseButton, {
        "aria-label": t('close'),
        "data-testid": "modal-close-btn",
        onClick: onClose,
        variant: "primary",
        icon: /*#__PURE__*/_jsx(CloseIcon, {
          size: 16
        }),
        size: "md",
        radius: "full",
        mode: "dark"
      })
    }), children, actionButtons && /*#__PURE__*/_jsx(ModalFooter, {
      children: actionButtons
    })]
  });

  return /*#__PURE__*/_jsx(AnimatePresence, {
    mode: "wait",
    children: isModalOpen && /*#__PURE__*/_jsx(ReactModal, {
      contentLabel: title,
      isOpen: isModalOpen,
      onRequestClose: onModalClose,
      shouldFocusAfterRender: shouldFocusAfterRender,
      style: mergedStyle,
      testId: testId,
      shouldCloseOnEsc: shouldCloseOnEsc,
      aria: aria,
      shouldCloseOnOverlayClick: true,
      overlayRef: function overlayRef(node) {
        return _overlayRef === null || _overlayRef === void 0 ? void 0 : _overlayRef(node);
      },
      children: wrapContent ? /*#__PURE__*/_jsx(Wrap, {
        className: className,
        fullScreenMobile: fullScreenMobile,
        maxWidth: maxWidth,
        bgColor: bgColor,
        "data-testid": props['data-testid'],
        "data-gaid": props['data-gaid'],
        children: modalBody
      }) : modalBody
    })
  });
};
var Wrap = styled.div.withConfig({
  componentId: "sc-e39h07-0"
})(["border-radius:", ";flex:1;margin-top:", ";position:relative;", ";", " ", " ", ""], getRadii('large'), getSpace('normal1'), maxWidthMixin, function (_ref2) {
  var fullScreenMobile = _ref2.fullScreenMobile;
  return fullScreenMobile && css(["border-radius:0;margin-top:0;", " @media screen and (max-width:", "px){max-width:100%;}"], media.md(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n        border-radius: ", ";\n        margin-top: ", ";\n      "])), getRadii('large'), getSpace('normal1')), breakpoints.md);
}, function (_ref3) {
  var bgColor = _ref3.bgColor;
  return bgColor && css(["background-color:", ";"], bgColor);
}, media.md(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    align-self: center;\n    margin-top: 0;\n  "]))));
var Aside = styled(motion.aside).withConfig({
  componentId: "sc-e39h07-1"
})(["height:100%;display:flex;flex-direction:column;"]);
var CloseButton = styled(ButtonIcon).withConfig({
  componentId: "sc-e39h07-2"
})(["&&{position:absolute;right:", ";top:", ";z-index:", ";", "}"], getSpace('small2'), getSpace('small2'), getZIndex('modal'), media.md(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      right: ", ";\n      top: ", ";\n    "])), getSpace('normal1'), getSpace('normal1')));
export var ModalFooter = styled.div.withConfig({
  componentId: "sc-e39h07-3"
})(["display:flex;flex-direction:column;padding:", " ", ";", " ", ""], getSpace('normal1'), getSpace('normal3'), media.md(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    flex-direction: row-reverse;\n  "]))), function (_ref4) {
  var escapeContainer = _ref4.escapeContainer;
  return escapeContainer && css(["margin-top:", ";margin-left:-", ";margin-right:-", ";"], getSpace('normal3'), getSpace('normal3'), getSpace('normal3'));
});
export var ModalActionButtons = styled(Grid).withConfig({
  componentId: "sc-e39h07-4"
})(["&&{grid-template-columns:1fr;", "}"], media.md(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      grid-template-columns: max-content max-content;\n    "]))));